import React, { useState, useEffect, useCallback } from "react";
import { apiAxios as axios } from "../../utils/apiClient.js";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import "./ProductSearch.css";
import { useUserLocation } from "../../utils/useUserLocation.js";
import {
  FiSearch,
  FiFilter,
  FiStar,
  FiMapPin,
  FiDollarSign,
  FiX,
  FiCheck,
} from "react-icons/fi";
import {
  FaStar,
  FaRegStar,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaMapMarkerAlt,
  FaClock,
} from "react-icons/fa";
import { url } from "../../Constant/TextConstant";
import Header from "../Header.jsx";
import Footer from "../Footer/Footer.jsx";

const ProductSearch = () => {
  const { searchterm } = useParams();
  const searchTerm = decodeURIComponent(searchterm || "");
  const navigate = useNavigate();

  const [state, setState] = useState({
    loading: true,
    error: null,
    products: [],
    filteredProducts: [],
    displayedProducts: [],
    hasMore: true,
    isLoadingMore: false,
    currentPage: 1,
    itemsPerPage: 10,
    initialItemsPerPage: 15,
    filters: {
      price: null,
      rating: null,
      distance: null,
    },
    sortOption: null,
    userLocation: null,
  });

  // Filter options
  const priceOptions = [
    { value: "under_500", label: "Under ₹500", range: [0, 500] },
    { value: "500_1000", label: "₹500-1K", range: [500, 1000] },
    { value: "1000_5000", label: "₹1K-5K", range: [1000, 5000] },
    { value: "over_5000", label: "Over ₹5K", range: [5000, Infinity] },
  ];

  const ratingOptions = [
    { value: "5", label: "5 ★", range: [5, 5] },
    { value: "4_5", label: "4-5 ★", range: [4, 5] },
    { value: "3_4", label: "3-4 ★", range: [3, 4] },
    { value: "under_3", label: "Below 3 ★", range: [0, 3] },
  ];

  const distanceOptions = [
    { value: "under_200", label: "<200m", range: [0, 0.2] },
    { value: "200_500", label: "200-500m", range: [0.2, 0.5] },
    { value: "500_1000", label: "500m-1km", range: [0.5, 1] },
    { value: "over_1000", label: ">1km", range: [1, Infinity] },
  ];

  const sortOptions = [
    {
      value: "distance_near",
      label: "Distance: Near to Far",
      icon: <FaSortUp />,
    },
    {
      value: "distance_far",
      label: "Distance: Far to Near",
      icon: <FaSortDown />,
    },
    { value: "price_low", label: "Price: Low to High", icon: <FaSortUp /> },
    { value: "price_high", label: "Price: High to Low", icon: <FaSortDown /> },
    { value: "rating_high", label: "Rating: High to Low", icon: <FaStar /> },
    { value: "rating_low", label: "Rating: Low to High", icon: <FaRegStar /> },
  ];

  // Modal states
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showSortModal, setShowSortModal] = useState(false);
  const [activeFilterTab, setActiveFilterTab] = useState("price");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
 // Replace the existing location state with the useUserLocation hook
   const {
     location: currentLocation,
     error: locationError,
     isLoading: isLocationLoading,
     updateLocation,
   } = useUserLocation();
 
   const [showModal, setShowModal] = useState(false);
   const [selectedCategory, setSelectedCategory] = useState(() => {
     return localStorage.getItem("selectedCategory") || "products";
   });
    useEffect(() => {
       window.scrollTo(0, 0);
     }, []);
 
   // Update localStorage whenever selectedCategory changes
   useEffect(() => {
     localStorage.setItem("selectedCategory", selectedCategory);
   }, [selectedCategory]);
 
   // Handler for modal close with location update
   const handleModalClose = (shouldClose, newCoordinates = null) => {
     setShowModal(false);
     if (newCoordinates) {
       // Update location through the hook's update function
       updateLocation({
         coordinates: {
           latitude: newCoordinates.latitude,
           longitude: newCoordinates.longitude,
         },
         address: newCoordinates.address || "",
         details: newCoordinates.details || null,
       });
     }
   };

   
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Get user location and fetch products
  const fetchProducts = useCallback(
    async (lat = null, lng = null) => {
      try {
        const mobile = localStorage.getItem("userMobile") || "";
        const userRole = "Customer";
        const productUrl = `${url.baseAPIUrl}/api/BindingProducts/GetSearchedProducts?userRole=${userRole}&searchterm=${searchTerm}&mobile=${mobile}&userLatitude=${lat}&userLongitude=${lng}`;

        const response = await axios.get(productUrl);
        const products = response.data.map((item) => ({
          ...item,
          id: item.id || Math.random().toString(36).substr(2, 9),
          OurPrice: parseFloat(item.OurPrice) || 0,
          Mrp: parseFloat(item.Mrp) || 0,
          AverageRating: parseFloat(item.AverageRating) || 0,
          Awaykm: parseFloat(item.Awaykm) || 0,
          Discount: parseFloat(item.Discount) || 0,
          TimeToReach: item.TimeToReach || "N/A",
          Image1: item.Image1 || "placeholder-product.jpg",
        }));

        setState((prev) => ({
          ...prev,
          loading: false,
          products,
          filteredProducts: [...products],
          displayedProducts: products.slice(0, prev.initialItemsPerPage),
          hasMore: products.length > prev.initialItemsPerPage,
          userLocation: lat && lng ? { lat, lng } : null,
        }));
      } catch (error) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: "Failed to load products. Please try again later.",
        }));
      }
    },
    [searchTerm]
  );

  useEffect(() => {
    const getUserLocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            fetchProducts(position.coords.latitude, position.coords.longitude);
          },
          (error) => {
            setState((prev) => ({
              ...prev,
              error: "Location access denied. Showing all products.",
            }));
            fetchProducts();
          }
        );
      } else {
        setState((prev) => ({
          ...prev,
          error: "Geolocation not supported. Showing all products.",
        }));
        fetchProducts();
      }
    };

    getUserLocation();
  }, [fetchProducts]);

  // Apply filters
  const applyFilters = (price = null, rating = null, distance = null) => {
    let filtered = [...state.products];

    filtered = filtered.filter((product) => {
      const priceMatch =
        !price ||
        (product.OurPrice >= price.range[0] &&
          product.OurPrice <= price.range[1]);

      const ratingMatch =
        !rating ||
        (product.AverageRating >= rating.range[0] &&
          product.AverageRating <= rating.range[1]);

      const distanceMatch =
        !distance ||
        (product.Awaykm >= distance.range[0] &&
          product.Awaykm <= distance.range[1]);

      return priceMatch && ratingMatch && distanceMatch;
    });

    setState((prev) => ({
      ...prev,
      filteredProducts: filtered,
      displayedProducts: filtered.slice(0, prev.initialItemsPerPage),
      filters: { price, rating, distance },
      currentPage: 1,
      hasMore: filtered.length > prev.initialItemsPerPage,
      sortOption: null,
    }));
  };

  // Clear filters
  const clearFilters = () => {
    setState((prev) => ({
      ...prev,
      filteredProducts: [...prev.products],
      displayedProducts: prev.products.slice(0, prev.initialItemsPerPage),
      filters: { price: null, rating: null, distance: null },
      currentPage: 1,
      hasMore: prev.products.length > prev.initialItemsPerPage,
      sortOption: null,
    }));
  };

  // Apply sort
  const applySort = (sortOption) => {
    let sorted = [...state.filteredProducts];

    switch (sortOption.value) {
      case "distance_near":
        sorted.sort((a, b) => (a.Awaykm || Infinity) - (b.Awaykm || Infinity));
        break;
      case "distance_far":
        sorted.sort((a, b) => (b.Awaykm || 0) - (a.Awaykm || 0));
        break;
      case "price_low":
        sorted.sort((a, b) => a.OurPrice - b.OurPrice);
        break;
      case "price_high":
        sorted.sort((a, b) => b.OurPrice - a.OurPrice);
        break;
      case "rating_high":
        sorted.sort((a, b) => (b.AverageRating || 0) - (a.AverageRating || 0));
        break;
      case "rating_low":
        sorted.sort((a, b) => (a.AverageRating || 0) - (b.AverageRating || 0));
        break;
      default:
        break;
    }

    setState((prev) => ({
      ...prev,
      filteredProducts: sorted,
      displayedProducts: sorted.slice(0, prev.initialItemsPerPage),
      sortOption,
      currentPage: 1,
      hasMore: sorted.length > prev.initialItemsPerPage,
    }));
  };

  // Clear sort
  const clearSort = () => {
    setState((prev) => ({
      ...prev,
      sortOption: null,
      filteredProducts: [...prev.products],
      displayedProducts: prev.products.slice(0, prev.initialItemsPerPage),
      currentPage: 1,
      hasMore: prev.products.length > prev.initialItemsPerPage,
    }));
  };

  // Load more products
  const loadMoreProducts = () => {
    if (!state.hasMore || state.isLoadingMore) return;

    const startIndex = state.displayedProducts.length;
    const endIndex = Math.min(
      startIndex + state.itemsPerPage,
      state.filteredProducts.length
    );

    setState((prev) => ({ ...prev, isLoadingMore: true }));

    setTimeout(() => {
      const newProducts = [
        ...state.displayedProducts,
        ...state.filteredProducts.slice(startIndex, endIndex),
      ];

      setState((prev) => ({
        ...prev,
        displayedProducts: newProducts,
        isLoadingMore: false,
        hasMore: endIndex < prev.filteredProducts.length,
        currentPage: prev.currentPage + 1,
      }));
    }, 500);
  };

  // Handle scroll for infinite loading
  const handleScroll = useCallback(() => {
    if (
      window.innerHeight + document.documentElement.scrollTop !==
      document.documentElement.offsetHeight
    )
      return;

    loadMoreProducts();
  }, [state.hasMore, state.isLoadingMore]);

  // Initialize scroll listener
  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);


  const handleProductClick = (product) => {
    const slug = createProductSlug(product.ProductName);
    navigate(`/product/${product.ID}/${slug}`, {
      state: { product },
    });
  };

  // Enhanced slug creation function
  const createProductSlug = (productName) => {
    return (
      productName
        .toLowerCase()
        .trim()
        // First replace % with '-percent-' to make it readable
        .replace(/%/g, "-percent-")
        // Then replace + with '-plus-'
        .replace(/\+/g, "-plus-")
        // Remove other special characters except hyphens
        .replace(/[^\w\s-]/g, "")
        // Replace spaces with hyphens
        .replace(/\s+/g, "-")
        // Collapse multiple hyphens
        .replace(/-+/g, "-")
        // Limit length
        .slice(0, 60)
        // Remove trailing hyphen
        .replace(/-$/, "")
    );
  };

  // Render loading state
  if (state.loading) {
    return (
      <>
        {" "}
        <Header
          userLocation={currentLocation} // Use currentLocation from hook
          setShowModal={setShowModal}
          setUserLocation={updateLocation} // Use updateLocation from hook
          showModal={showModal}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
        />
        <div className="product-search-container">
          <div className="product-search-loading-spinner">
            <div className="product-search-spinner"></div>
            <p>Loading products...</p>
          </div>
    
        </div>
              <Footer />
      </>

    );
  }

  // Render error state
  if (state.error) {
    return (
      <>
        {" "}
        <Header
          userLocation={currentLocation} // Use currentLocation from hook
          setShowModal={setShowModal}
          setUserLocation={updateLocation} // Use updateLocation from hook
          showModal={showModal}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
        />
        <div className="product-search-container">
          <div className="product-search-error-message">
            <FiX className="product-search-error-icon" />
            <p>{state.error}</p>
            <button
              className="product-search-retry-button"
              onClick={() => window.location.reload()}
            >
              Try Again
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Render no results
  if (state.filteredProducts.length === 0) {
    return (
      <>
        {" "}
        <Header
          userLocation={currentLocation} // Use currentLocation from hook
          setShowModal={setShowModal}
          setUserLocation={updateLocation} // Use updateLocation from hook
          showModal={showModal}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
        />
        <div className="product-search-container">
          <div className="product-search-no-results">
            <FiSearch className="product-search-no-results-icon" />
            <h3>No Products Found</h3>
            <p>We couldn't find any results for "{searchTerm}"</p>
            <button
              className="product-search-clear-filters-button"
              onClick={clearFilters}
            >
              Clear Filters
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {searchTerm ? `${searchTerm} - Search Results` : "Product Search"} |
          MyStore
        </title>
        <meta
          name="description"
          content={`Find the best products matching "${searchTerm}". Filter by price, rating, and distance. Shop now for great deals!`}
        />
        <meta
          name="keywords"
          content={`${searchTerm}, products, shopping, deals, discounts, online store`}
        />
        <meta
          property="og:title"
          content={`${searchTerm} - Search Results | MyStore`}
        />
        <meta
          property="og:description"
          content={`Browse products matching "${searchTerm}" on MyStore`}
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta
          property="og:image"
          content={`${window.location.origin}/logo.png`}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`${searchTerm} - Search Results | MyStore`}
        />
        <meta
          name="twitter:description"
          content={`Browse products matching "${searchTerm}" on MyStore`}
        />
        <meta
          name="twitter:image"
          content={`${window.location.origin}/logo.png`}
        />
        <link rel="canonical" href={window.location.href} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SearchResultsPage",
            name: `Search Results for "${searchTerm}"`,
            description: `Products matching "${searchTerm}"`,
            url: window.location.href,
            potentialAction: {
              "@type": "SearchAction",
              target: `${window.location.origin}/search?q={search_term_string}`,
              "query-input": "required name=search_term_string",
            },
          })}
        </script>
      </Helmet>
      <Header
        userLocation={currentLocation} // Use currentLocation from hook
        setShowModal={setShowModal}
        setUserLocation={updateLocation} // Use updateLocation from hook
        showModal={showModal}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
      />
      <div className="product-search-container">
        <div className="product-search-content">
          <div className="product-search-header">
            <h2 className="product-search-title">
              Searching for{" "}
              <span className="product-search-term">"{searchTerm}"</span>
            </h2>
            <p className="product-search-results-count">
              {state.filteredProducts.length}{" "}
              {state.filteredProducts.length === 1 ? "result" : "results"}
            </p>
          </div>

          <div className="product-search-filter-sort-bar">
            <button
              className={`product-search-sort-button ${
                state.sortOption ? "product-search-active" : ""
              }`}
              onClick={() => setShowSortModal(true)}
            >
              <FaSort className="product-search-sort-icon" />
              <span>Sort</span>
              {state.sortOption && (
                <FiCheck className="product-search-active-icon" />
              )}
            </button>

            <div className="product-search-filter-buttons">
              <button
                className={`product-search-filter-button ${
                  state.filters.price ? "product-search-active" : ""
                }`}
                onClick={() => {
                  setActiveFilterTab("price");
                  setShowFilterModal(true);
                }}
              >
                <FiDollarSign className="product-search-filter-icon" />
                <span>Price</span>
                {state.filters.price && (
                  <FiCheck className="product-search-active-icon" />
                )}
              </button>

              <button
                className={`product-search-filter-button ${
                  state.filters.rating ? "product-search-active" : ""
                }`}
                onClick={() => {
                  setActiveFilterTab("rating");
                  setShowFilterModal(true);
                }}
              >
                <FiStar className="product-search-filter-icon" />
                <span>Rating</span>
                {state.filters.rating && (
                  <FiCheck className="product-search-active-icon" />
                )}
              </button>

              <button
                className={`product-search-filter-button ${
                  state.filters.distance ? "product-search-active" : ""
                }`}
                onClick={() => {
                  setActiveFilterTab("distance");
                  setShowFilterModal(true);
                }}
              >
                <FiMapPin className="product-search-filter-icon" />
                <span>Distance</span>
                {state.filters.distance && (
                  <FiCheck className="product-search-active-icon" />
                )}
              </button>
            </div>
          </div>

          <div className="product-search-grid">
            {state.displayedProducts.map((product) => (
              <div
                key={product.id}
                className={`product-search-card ${
                  isMobile ? "mobile-view" : ""
                }`}
                onClick={() => handleProductClick(product)}
              >
                <div className="product-search-image-container">
                  <img
                    src={`${url.productImageStartingPath}/${product.Image1}`}
                    alt={product.ProductName}
                    className="product-search-image"
                    loading="lazy"
                  />
                  {product.Discount > 0 && (
                    <div className="product-search-discount-badge">
                      {product.Discount}% OFF
                    </div>
                  )}
                </div>

                <div className="product-search-details">
                  <h3 className="product-search-name">{product.ProductName}</h3>
                  <p className="product-search-store">{product.StoreName}</p>

                  <div className="product-search-meta">
                    {product.AverageRating>0&&(
                      <div className="product-search-rating">
                        <FaStar className="product-search-star-icon" />
                        <span>{product.AverageRating || ""}</span>
                      </div>
                    )}

                    <div className="product-search-distance">
                      <FaMapMarkerAlt className="product-search-pin-icon" />
                      <span>{product.Awaykm?.toFixed(2) || 0} km</span>
                    </div>

                    <div className="product-search-time">
                      <FaClock className="product-search-time-icon" />
                      <span>{product.TimeToReach}</span>
                    </div>
                  </div>

                  <div className="product-search-price">
                    <span className="product-search-current-price">
                      ₹{product.OurPrice}
                    </span>
                    {product.Mrp > product.OurPrice && (
                      <span className="product-search-original-price">
                        ₹{product.Mrp}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {state.isLoadingMore && (
            <div className="product-search-loading-more">
              <div className="product-search-spinner product-search-small"></div>
              <p>Loading more products...</p>
            </div>
          )}

          {!state.hasMore && state.displayedProducts.length > 0 && (
            <div className="product-search-no-more-products">
              <p>You've reached the end of the list</p>
            </div>
          )}
        </div>

        {/* Filter Modal */}
        {showFilterModal && (
          <div className="product-search-modal-overlay">
            <div className="product-search-filter-modal">
              <div className="product-search-modal-header">
                <h3>Filter Products</h3>
                <button
                  className="product-search-close-modal"
                  onClick={() => setShowFilterModal(false)}
                >
                  <FiX />
                </button>
              </div>
              <div className="product-search-filter-tabs">
                <div
                  className={`product-search-filter-tab ${
                    activeFilterTab === "price" ? "product-search-active" : ""
                  }`}
                  onClick={() => setActiveFilterTab("price")}
                >
                  <FiDollarSign /> Price
                </div>
                <div
                  className={`product-search-filter-tab ${
                    activeFilterTab === "rating" ? "product-search-active" : ""
                  }`}
                  onClick={() => setActiveFilterTab("rating")}
                >
                  <FiStar /> Rating
                </div>
                <div
                  className={`product-search-filter-tab ${
                    activeFilterTab === "distance"
                      ? "product-search-active"
                      : ""
                  }`}
                  onClick={() => setActiveFilterTab("distance")}
                >
                  <FiMapPin /> Distance
                </div>
              </div>
              <div className="product-search-filter-options">
                {(activeFilterTab === "price"
                  ? priceOptions
                  : activeFilterTab === "rating"
                  ? ratingOptions
                  : distanceOptions
                ).map((option) => (
                  <div
                    key={option.value}
                    className={`product-search-filter-option ${
                      (activeFilterTab === "price" &&
                        state.filters.price?.value === option.value) ||
                      (activeFilterTab === "rating" &&
                        state.filters.rating?.value === option.value) ||
                      (activeFilterTab === "distance" &&
                        state.filters.distance?.value === option.value)
                        ? "product-search-selected"
                        : ""
                    }`}
                    onClick={() => {
                      if (activeFilterTab === "price") {
                        applyFilters(
                          state.filters.price?.value === option.value
                            ? null
                            : option,
                          state.filters.rating,
                          state.filters.distance
                        );
                      } else if (activeFilterTab === "rating") {
                        applyFilters(
                          state.filters.price,
                          state.filters.rating?.value === option.value
                            ? null
                            : option,
                          state.filters.distance
                        );
                      } else {
                        applyFilters(
                          state.filters.price,
                          state.filters.rating,
                          state.filters.distance?.value === option.value
                            ? null
                            : option
                        );
                      }
                    }}
                  >
                    <span>{option.label}</span>
                    {(activeFilterTab === "price" &&
                      state.filters.price?.value === option.value) ||
                    (activeFilterTab === "rating" &&
                      state.filters.rating?.value === option.value) ||
                    (activeFilterTab === "distance" &&
                      state.filters.distance?.value === option.value) ? (
                      <FiCheck className="product-search-check-icon" />
                    ) : null}
                  </div>
                ))}
              </div>
              <div className="product-search-modal-footer">
                <button
                  className="product-search-secondary-button"
                  onClick={clearFilters}
                >
                  Clear All
                </button>
                <button
                  className="product-search-primary-button"
                  onClick={() => setShowFilterModal(false)}
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sort Modal */}
        {showSortModal && (
          <div className="product-search-modal-overlay">
            <div className="product-search-sort-modal">
              <div className="product-search-modal-header">
                <h3>
                  <FaSort /> Sort By
                </h3>
                <button
                  className="product-search-close-modal"
                  onClick={() => setShowSortModal(false)}
                >
                  <FiX />
                </button>
              </div>
              <div className="product-search-sort-options">
                {sortOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`product-search-sort-option ${
                      state.sortOption?.value === option.value
                        ? "product-search-selected"
                        : ""
                    }`}
                    onClick={() => {
                      applySort(option);
                      setShowSortModal(false);
                    }}
                  >
                    <span className="product-search-sort-icon">
                      {option.icon}
                    </span>
                    <span>{option.label}</span>
                    {state.sortOption?.value === option.value && (
                      <FiCheck className="product-search-check-icon" />
                    )}
                  </div>
                ))}
              </div>
              <div className="product-search-modal-footer">
                <button
                  className="product-search-secondary-button product-search-full-width"
                  onClick={() => {
                    clearSort();
                    setShowSortModal(false);
                  }}
                >
                  Clear Sort
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

export default ProductSearch;
